import { scaleLinear, scaleBand, scaleOrdinal } from 'd3-scale';
import { select } from 'd3-selection';
import { easeCircleOut, easeBackOut } from 'd3-ease';
import { sum, max } from 'd3-array';
import { arc, stack } from 'd3-shape';
import 'd3-transition';
import { formatWindGust } from './formatters';

class WindRose {

	constructor(data, speedUnit) {
		this.data = data;
		this.unit = speedUnit;
	}

	render(containerNode) {
		this._init(containerNode);
		this._setupScales();
		this._renderSpokes();
		this._renderAxis();
		this._renderDirectionLines();
		this._renderLegend();
	}

	_init(containerNode) {

		this.container = select(containerNode);
		const containerSize = containerNode.getBoundingClientRect();

		this.dim = {
			marginTop: 20,
			marginBottom: 20,
			marginLeft: 20,
			marginRight: 20,
			innerRadius: containerSize.width * 0.025,
			legendWidth: 84,
			directionLabelSpacing: 20,
			totalWidth: containerSize.width
		};

		this.dim.totalHeight = this.dim.totalWidth - this.dim.legendWidth;

		this.dim.roseSize = this.dim.totalWidth - this.dim.legendWidth - this.dim.marginLeft - this.dim.marginRight - 2 * this.dim.directionLabelSpacing;

		this.dim.roseCentroid = [
			0.5 * this.dim.roseSize + this.dim.directionLabelSpacing + this.dim.marginLeft,
			0.5 * this.dim.roseSize + this.dim.directionLabelSpacing + this.dim.marginTop
		];

		this.dim.outerRadius = 0.5 * this.dim.roseSize - 10;

		this.angleOffset = -180 / this.data.spokes.length;

		this.container.html('');

		this.svg = this.container.append('svg')
			.attr('width', this.dim.totalWidth)
			.attr('height', this.dim.totalHeight);

		this.windRose = this.svg.append('g')
			.attr('transform', `translate(${this.dim.roseCentroid[0]},${this.dim.roseCentroid[1]})`);

	}

	_setupScales() {

		this.thetaScale = scaleBand()
			.domain(this.data.spokes.map(spoke => spoke.direction))
			.range([0, 2 * Math.PI])
			.align(0);

		this.radiusScale = scaleLinear()
			.domain([0, max(this.data.spokes.map(spoke => sum(spoke.values)))])
			.range([this.dim.innerRadius, this.dim.outerRadius]);

		this.colourScale = scaleOrdinal()
			.domain(this.data.categories)
			.range([
				'#4242f4',
				'#42c5f4',
				'#42f4ce',
				'#42f456',
				'#adf442',
				'#f4e242',
				'#f4a142',
				'#f44242'
			]);

	}

	_renderSpokes() {

		const stackLayout = stack()
			.keys(this.data.categories);

		const stackData = this.data.spokes.map(spoke => {
			const item = { direction: spoke.direction };
			this.data.categories.forEach((c, i) => {
				item[c] = spoke.values[i];
			});
			return item;
		});

		this.windRose.append('g')
			.attr('class', 'wind-rose-spokes')
			.selectAll('g')
			.data(stackLayout(stackData))
			.enter()
			.append('g')
			.style('fill', d => this.colourScale(d.key))
			.selectAll('path')
			.data(d => d)
			.enter()
			.append('path')
			.attr('transform', `rotate(${this.angleOffset})`)
			.transition()
			.ease(easeBackOut)
			.delay((d, i) => i * 50)
			.duration(300)
			.attrTween('d', d => {
				// Return function that takes t from 0 to 1 (transition start to end) and generates the
				// arc to render at that time point.
				return t => arc()
					.innerRadius(t * this.radiusScale(d[0]))
					.outerRadius(t * this.radiusScale(d[1]))
					.startAngle(this.thetaScale(d.data.direction))
					.endAngle(this.thetaScale(d.data.direction) + 0.5 * (t + 1) * this.thetaScale.bandwidth())
					.padAngle(0.05)
					.padRadius(this.dim.innerRadius)();
			});
	}

	_renderDirectionLines() {

		this.windRose.append('circle')
			.attr('class', 'hw_wind_rose_center')
			.attr('cx', 0)
			.attr('cy', 0)
			.attr('r', this.radiusScale(0));

		const labels = this.windRose.append('g')
			.attr('class', 'hw_wind_rose_labels')
			.selectAll('g')
			.data(this.data.spokes)
			.enter()
			.append('g')
			.attr('text-anchor', 'middle')
			.attr('transform', spoke => {
				const angle = rad2deg(this.thetaScale(spoke.direction) + 0.5 * this.thetaScale.bandwidth()) + this.angleOffset - 90;
				return `rotate(${angle})`;
			});

		labels.transition()
			.ease(easeCircleOut)
			.duration(500)
			.attrTween('transform', spoke => {
				const translateX = this.dim.outerRadius + this.dim.directionLabelSpacing + 5;
				const angle = rad2deg(this.thetaScale(spoke.direction) + 0.5 * this.thetaScale.bandwidth()) + this.angleOffset - 90;
				return t => `rotate(${angle}) translate(${t * translateX},0)`;
			});

		labels.append('text')
			.attr('transform', spoke => {
				const angle = rad2deg(this.thetaScale(spoke.direction) + 0.5 * this.thetaScale.bandwidth()) + this.angleOffset - 90;
				if (angle < 0 || angle >= 180) {
					return 'translate(0,0) rotate(90)';
				}
				return 'translate(10,0) rotate(270)';
			})
			.text(spoke => spoke.direction);

		const lines = this.windRose.append('g')
			.attr('class', 'hw_wind_rose_lines')
			.selectAll('line')
			.data(this.data.spokes)
			.enter()
			.append('g')
			.attr('transform', spoke => `rotate(${rad2deg(this.thetaScale(spoke.direction))})`);

		lines.append('line')
			.attr('x1', 0)
			.attr('y1', this.dim.innerRadius)
			.attr('x2', 0)
			.attr('y2', this.dim.innerRadius)
			.transition()
			.ease(easeCircleOut)
			.duration(500)
			.attrTween('y2', () => {
				const endY = (0.5 * this.dim.roseSize + this.dim.directionLabelSpacing - 10);
				return t => t * endY;
			});

		lines.append('path')
			.transition()
			.ease(easeCircleOut)
			.duration(500)
			.attrTween('d', () => {
				const endRadius = 0.5 * this.dim.roseSize + this.dim.directionLabelSpacing - 10;
				return t => arc()
					.innerRadius(t * endRadius)
					.outerRadius(t * endRadius)
					.startAngle(deg2rad(-2))
					.endAngle(deg2rad(2))();
			});

	}

	_renderAxis() {

		const ticks = this.windRose.append('g')
			.attr('class', 'hw_wind_rose_axis')
			.attr('text-anchor', 'end')
			.selectAll('text')
			.data(this.radiusScale.ticks(5).slice(1))
			.enter();

		ticks.append('text')
			.attr('x', -3)
			.attr('y', value => -this.radiusScale(value))
			.attr('dy', '-0.25em')
			.text(value => value);

		ticks.append('circle')
			.attr('class', 'hw_wind_rose_gridline')
			.attr('cx', 0)
			.attr('cy', 0)
			.attr('r', value => this.radiusScale(value));

	}

	_renderLegend() {

		const boxSize = 16;
		const boxSpacing = 6;

		const legend = this.svg.append('g')
			.attr('class', 'hw_wind_rose_legend')
			.attr('transform', `translate(${this.dim.totalWidth - this.dim.legendWidth},${this.dim.marginTop})`);

		const title = legend.append('text')
			.attr('class', 'hw_wind_rose_legend_title');

		title.append('tspan')
			.attr('x', 0)
			.text('Mean wind');

		title.append('tspan')
			.attr('x', 0)
			.attr('dy', '1.2em')
			.text('speed (' + this.unit + ')');

		const legendItems = legend.selectAll('g')
			.data(this.data.categories)
			.enter()
			.append('g')
			.attr('transform', (d, i) => `translate(0,${24 + i * (boxSize + boxSpacing)})`);

		legendItems.append('rect')
			.attr('width', boxSize)
			.attr('height', boxSize)
			.style('fill', category => this.colourScale(category));

		const formatLegendValue = value => {
			return formatWindGust(value, this.unit);
		};

		legendItems.append('text')
			.attr('x', boxSize + boxSpacing)
			.attr('y', 0)
			.attr('dy', '1em')
			.text(category => {
				if (category[1] === null) {
					return formatLegendValue(category[0]) + '+';
				}
				return category.map(formatLegendValue).join('-');
			});

	}

}

function rad2deg(radians) {
	return 180 * radians / Math.PI;
}

function deg2rad(degrees) {
	return degrees * Math.PI / 180;
}

export default WindRose;



// WEBPACK FOOTER //
// ./utils/wind-rose.js